<template>
  <NavHeader />
  <div class="chat-history-container">
    <template
      v-if="
        todayConversations.length ||
        last7daysConversations.length ||
        olderConversations.length
      "
    >
      <div class="padding-b-4">
        <h5
          class="text-align-left padding-b-2"
          v-if="todayConversations.length > 0"
        >
          {{ $t('legal_assistant.chat_history_today') }}
        </h5>
        <TaskCard
          v-for="(conversation, index) in todayConversations"
          :key="index"
          :description="conversation.title!"
          :subtitle="getConversationSubtitle(conversation)"
          :icon="getIconForAssistant(conversation.assistant)"
          :icon-size="IconSize.HeadlineM"
          :focused="conversation.id === currentConversationId"
          @click="selectConversation(conversation.id!)"
        >
          <template #actions>
            <AppIcon
              :name="IconName.Trash"
              :size="IconSize.BodyL"
              :onClick="
                () =>
                  deleteConversation(
                    conversation.id,
                    conversation.assistant,
                    TODAY
                  )
              "
            />
          </template>
        </TaskCard>
      </div>
      <div class="padding-b-4">
        <h5
          class="text-align-left padding-b-2"
          v-if="last7daysConversations.length > 0"
        >
          {{ $t('legal_assistant.chat_history_last_7_days') }}
        </h5>
        <TaskCard
          v-for="(conversation, index) in last7daysConversations"
          :key="index"
          :description="conversation.title!"
          :subtitle="getConversationSubtitle(conversation)"
          :icon="getIconForAssistant(conversation.assistant)"
          :icon-size="IconSize.HeadlineM"
          :focused="conversation.id === currentConversationId"
          @click="selectConversation(conversation.id!)"
        >
          <template #actions>
            <AppIcon
              :name="IconName.Trash"
              :size="IconSize.BodyL"
              :onClick="
                () =>
                  deleteConversation(
                    conversation.id,
                    conversation.assistant,
                    LAST7
                  )
              "
            />
          </template>
        </TaskCard>
      </div>
      <div class="padding-b-4">
        <h5
          class="text-align-left padding-b-2"
          v-if="olderConversations.length > 0"
        >
          {{ $t('legal_assistant.chat_history_last_30_days') }}
        </h5>
        <TaskCard
          v-for="(conversation, index) in olderConversations"
          :key="index"
          :description="conversation.title!"
          :subtitle="getConversationSubtitle(conversation)"
          :icon="getIconForAssistant(conversation.assistant)"
          :icon-size="IconSize.HeadlineM"
          :focused="conversation.id === currentConversationId"
          @click="selectConversation(conversation.id!)"
        >
          <template #actions>
            <AppIcon
              :name="IconName.Trash"
              :size="IconSize.BodyL"
              :onClick="
                () =>
                  deleteConversation(
                    conversation.id,
                    conversation.assistant,
                    OLDER
                  )
              "
            />
          </template>
        </TaskCard>
      </div>
    </template>
    <template v-else>
      <div class="no-history">
        <p v-if="loadingHistory" class="text-align-center color-lighter">
          {{ $t('chat_history.loading') }}
        </p>
        <p v-else class="text-align-center color-lighter">
          {{ $t('chat_history.empty') }}
        </p>
      </div>
    </template>
  </div>

  <CustomModal
    :show="showDeleteConfirmationModal"
    @update:show="(val: boolean) => (showDeleteConfirmationModal = val)"
    :onClose="handleModalClose"
  >
    <div class="modal-content-wrapper">
      <h6>
        {{ $t('chat_history.delete_conversation_confirmation') }}
      </h6>
      <p>
        {{ $t('chat_history.delete_conversation_warning') }}
      </p>
    </div>
    <template #left-button>
      <FormButton
        :label="$t('common.confirm')"
        type="primary"
        :onClick="confirmDeleteConversation"
      />
    </template>
  </CustomModal>
</template>

<script setup lang="ts">
import AppIcon from '@/components/common/app-icon/AppIcon.vue';
import FormButton from '@/components/common/button/FormButton.vue';
import CustomModal from '@/components/common/modal/CustomModal.vue';
import NavHeader from '@/components/common/nav-header/NavHeader.vue';
import TaskCard from '@/components/common/task-card/TaskCard.vue';
import { IndexIdentifiers } from '@/core/indexesEnum';
import {
  AssistantType,
  ChatAssistant,
  ReviewingAssistantType
} from '@/models/assistant-types';
import { Conversation } from '@/models/conversation';
import { IconName, IconSize } from '@/models/icon-types';
import navigationService from '@/services/NavigationService';
import ChatHistoryService from '@/services/ChatHistoryService';
import { getFormatedDate } from '@/utils/utils';
import { onMounted, ref } from 'vue';
import RouteNames from '@/configuration/router/names.routes';

const todayConversations = ref<Conversation[]>([]);
const last7daysConversations = ref<Conversation[]>([]);
const olderConversations = ref<Conversation[]>([]);
const loadingHistory = ref(true);

const showDeleteConfirmationModal = ref(false);
const pendingDeleteConversationId = ref<string>('');
const pendingDeleteAssistant = ref<ChatAssistant>();
const pendingDeleteListType = ref<string>('');

const OLDER = 'older';
const TODAY = 'today';
const LAST7 = 'last7';

const props = defineProps<{
  assistant: ChatAssistant;
  currentConversationId?: string;
}>();

onMounted(async () => {
  loadingHistory.value = true;
  await fetchAndGroupConversations();
  loadingHistory.value = false;
});

function toUTCDateKey(date: Date): number {
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

async function fetchAndGroupConversations(): Promise<void> {
  const conversationsObj = await ChatHistoryService.getUserConversations(
    props.assistant
  );

  const conversations = Object.values(conversationsObj)
    .map((c) => {
      const dateTime =
        (c as Conversation).dateTime ?? (c as { datetime?: string }).datetime;
      return { ...c, dateTime } as Conversation;
    })
    .sort(
      (a, b) => new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime()
    );

  const todayKey = toUTCDateKey(new Date());

  todayConversations.value = conversations.filter(
    (c) => toUTCDateKey(new Date(c.dateTime)) === todayKey
  );

  last7daysConversations.value = conversations.filter((c) => {
    const diff =
      (todayKey - toUTCDateKey(new Date(c.dateTime))) / (1000 * 60 * 60 * 24);
    return diff >= 1 && diff <= 7;
  });

  olderConversations.value = conversations.filter((c) => {
    const diff =
      (todayKey - toUTCDateKey(new Date(c.dateTime))) / (1000 * 60 * 60 * 24);
    return diff > 7;
  });
}

async function selectConversation(id: string | undefined): Promise<void> {
  if (!id) return;
  navigationService.navigateTo({
    name: RouteNames.LegalAssistant,
    query: { conversationId: id }
  });
}

async function deleteConversation(
  id: string | undefined,
  assistant: ChatAssistant,
  listType: string
): Promise<void> {
  if (!id) return;
  pendingDeleteConversationId.value = id;
  pendingDeleteAssistant.value = assistant;
  pendingDeleteListType.value = listType;
  showDeleteConfirmationModal.value = true;
}

async function confirmDeleteConversation(): Promise<void> {
  showDeleteConfirmationModal.value = false;
  let listRef = null;
  if (pendingDeleteListType.value === TODAY) {
    listRef = todayConversations;
  } else if (pendingDeleteListType.value === LAST7) {
    listRef = last7daysConversations;
  } else if (pendingDeleteListType.value === OLDER) {
    listRef = olderConversations;
  }
  if (listRef) {
    removeConversationFromList(listRef, pendingDeleteConversationId.value);
  }
  try {
    await ChatHistoryService.deleteConversation(
      pendingDeleteConversationId.value
    );
  } catch (error) {
    console.error('Error deleting conversation:', error);
    await fetchAndGroupConversations();
  }
}

function removeConversationFromList(
  listRef: typeof todayConversations,
  conversationId: string
) {
  listRef.value = listRef.value.filter((c) => c.id !== conversationId);
}

function handleModalClose(): void {
  showDeleteConfirmationModal.value = false;
  pendingDeleteConversationId.value = '';
  pendingDeleteAssistant.value = undefined;
}

function getIconForAssistant(assistant: ChatAssistant): IconName {
  switch (assistant) {
    case AssistantType.LegalAssistant:
      return IconName.FileCloud;
    case ReviewingAssistantType.DocumentReviewingAssistant:
      return IconName.DocReview;
    default:
      return IconName.FileUser;
  }
}

function getConversationSubtitle(conversation: Conversation): string {
  const formattedDate = getFormatedDate(conversation.dateTime);

  if (conversation.ragIndex && IndexIdentifiers[conversation.ragIndex]) {
    return `${formattedDate} - ${IndexIdentifiers[conversation.ragIndex]}`;
  }

  return formattedDate;
}
</script>

<style scoped lang="scss">
.chat-history-container {
  flex: 1;
  overflow-y: auto;
  height: calc(100vh - 48px);
  padding: $space-1;

  @media (max-height: $small-screen-height) {
    height: calc(100vh - 40px);
  }
}

.no-history {
  flex-grow: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: $space-1;
}

.modal-content-wrapper {
  padding: $space-2 0;
}

.modal-content-wrapper h6 {
  margin-bottom: $space-2;
  color: $color-neutral-900;
}

.modal-content-wrapper p {
  margin-bottom: 0;
  color: $color-neutral-700;
}

:deep(.task-icon-container .app-icon) {
  transition: transform 0.2s ease;
  cursor: pointer;

  &:hover {
    transform: scale(1.15);
  }
}
</style>
